// Demand-aware auction starting-bid suggestion.
//
// Given simple demand counts from eBay (active listings vs. recently sold),
// classify the card into a demand tier and return the starting-bid ratio to
// apply to the seller's target sale price. The "unknown" tier maps to the
// medium ratio so behavior is never worse than a flat 35% baseline when we
// can't fetch demand data.

export type DemandTier = "high" | "medium" | "low" | "unknown";

export const HIGH_DEMAND_RATIO = 0.5;
export const MEDIUM_DEMAND_RATIO = 0.35;
export const LOW_DEMAND_RATIO = 0.2;
export const UNKNOWN_DEMAND_RATIO = MEDIUM_DEMAND_RATIO;

export const DEMAND_TIER_RATIO: Record<DemandTier, number> = {
  high: HIGH_DEMAND_RATIO,
  medium: MEDIUM_DEMAND_RATIO,
  low: LOW_DEMAND_RATIO,
  unknown: UNKNOWN_DEMAND_RATIO,
};

export interface DemandCounts {
  activeCount: number | null;
  recentSoldCount: number | null;
}

export interface DemandSuggestion {
  tier: DemandTier;
  startBidRatio: number;
}

/**
 * Score = recentSoldCount / activeCount when both are positive numbers.
 *  > 0.7 → high
 *  0.3..0.7 → medium
 *  < 0.3 → low
 * Missing data, unparseable counts, or zero active listings → unknown
 * (treated as medium ratio so the suggestion is safe).
 */
export function classifyDemand(counts: DemandCounts): DemandSuggestion {
  const { activeCount, recentSoldCount } = counts;
  if (
    activeCount == null ||
    recentSoldCount == null ||
    !Number.isFinite(activeCount) ||
    !Number.isFinite(recentSoldCount) ||
    activeCount <= 0
  ) {
    return { tier: "unknown", startBidRatio: UNKNOWN_DEMAND_RATIO };
  }
  // recentSoldCount === 0 is meaningful: real demand signal of "no sales" → low.
  const score = recentSoldCount / activeCount;
  if (score > 0.7) return { tier: "high", startBidRatio: HIGH_DEMAND_RATIO };
  if (score >= 0.3)
    return { tier: "medium", startBidRatio: MEDIUM_DEMAND_RATIO };
  return { tier: "low", startBidRatio: LOW_DEMAND_RATIO };
}

const TIER_LABEL: Record<DemandTier, string> = {
  high: "high demand",
  medium: "medium demand",
  low: "low demand",
  unknown: "demand unknown",
};

/**
 * Helper text shown under the auction starting-bid input. Keeps the format
 * consistent across the form and tests.
 */
export function describeDemandSuggestion(
  suggestion: DemandSuggestion,
  targetPrice: number,
): string {
  const pct = Math.round(suggestion.startBidRatio * 100);
  const label = TIER_LABEL[suggestion.tier];
  if (suggestion.tier === "unknown") {
    return `${pct}% of target · ${label}`;
  }
  return `${pct}% of target · ${label} · $${targetPrice.toFixed(2)}`;
}
